<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import long2ip from 'locutus/php/network/long2ip'
import strtotime from 'locutus/php/datetime/strtotime'
import PageNavigation from '@components/admin/page-navigation'
import { fetchBalanceLogList, setAccountBalance } from '@apis/admin/account'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import Decimal from 'decimal.js'
import LangItem from '@components/admin/lang-input'
import pick from 'lodash/pick'

export default {
  components: { PageNavigation, LangItem },
  data() {
    return {
      loading: false,
      list: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      balance_params: {
        amount: '',
        description: '',
        action: null,
      },
      excel_headers: [
        ['seq', this.$t('account_balance_log_list.seq')],
        ['eventer_seq', this.$t('account_balance_log_list.eventer_seq')],
        ['description', this.$t('account_balance_log_list.description')],
        ['ipaddress', this.$t('account_balance_log_list.ipaddress')],
        ['user_agent', this.$t('account_balance_log_list.user_agent')],
        ['default_locale', this.$t('account_balance_log_list.default_locale')],
        ['regdate', this.$t('account_balance_log_list.regdate')],
        ['logset_seq', this.$t('account_balance_log_list.logset_seq')],
        ['target', this.$t('account_balance_log_list.target')],
        ['target_seq', this.$t('account_balance_log_list.target_seq')],
        ['reference', this.$t('account_balance_log_list.reference')],
        ['reference_seq', this.$t('account_balance_log_list.reference_seq')],
        [
          'logset_description',
          this.$t('account_balance_log_list.logset_description'),
        ],
        ['user_id', this.$t('account_balance_log_list.user_id')],
        ['email_address', this.$t('account_balance_log_list.email_address')],
        ['user_name', this.$t('account_balance_log_list.user_name')],
        ['before', this.$t('account_balance_log_list.before')],
        ['after', this.$t('account_balance_log_list.after')],
        [
          'logset_description_replaced',
          this.$t('account_balance_log_list.logset_description_replaced'),
        ],
        [
          'description_replaced',
          this.$t('account_balance_log_list.description_replaced'),
        ],
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('admin.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchBalanceLogList({
        ...this.$route.params,
        ...this.$route.query,
      })

      map(ret_obj.data, (val) => {
        const diff = new Decimal(val.after).minus(val.before)

        if (diff.gt(0)) val.heading = 'up'
        else if (diff.lt(0)) val.heading = 'down'
        else val.heading = 'eq'

        val.diff = diff.abs().toString()
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    async handleSetBalance() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.msg_balance_adjustment_ask'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await setAccountBalance({
          ...this.balance_params,
          account_seq: this.$route.params.account_seq,
        })

        this.balance_params = {
          amount: '',
          description: '',
          action: null,
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_balance_adjustment_succeed'),
          type: 'success',
        })

        await this.loadList()
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.msg_excel_warning'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchBalanceLogList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('account_balance_log_list.excel_filename', [
            date('YmdHis'),
          ]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'target':
              case 'reference':
                return fromPairs(this.constants.log.log.target)[val]
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
    date,
    fromPairs,
    long2ip,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.balance_adjustment') }}</h3>
    <div :class="$style.infoText">
      <p>{{ $t('admin.msg_balance_forced_adjustment_info') }}</p>
      <p :class="$style.warning">{{
        $t('admin.msg_balance_forced_adjustment_responsibility')
      }}</p>
      <p :class="$style.warning">{{
        $t('admin.msg_balance_forced_adjustment_error')
      }}</p>
      <p :class="$style.warning">{{
        $t('admin.msg_balance_forced_adjustment_carefully')
      }}</p>
    </div>
    <form @submit.prevent="handleSetBalance">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                {{ $t('admin.adjustment_amount') }}
              </div>
            </th>
            <td>
              <input
                v-model="balance_params.amount"
                :placeholder="0"
                type="text"
                :class="[$style.inputTypeText, $style.w100, $style.tRight]"
              />
              <ElRadioGroup
                v-model="balance_params.action"
                :class="$style.fr"
                size="mini"
              >
                <ElRadioButton
                  v-for="val in [
                    ['PLUS', $t('admin.increase')],
                    ['MINUS', $t('admin.deduction')],
                  ]"
                  :key="val[0]"
                  :label="val[0]"
                  >{{ val[1] }}</ElRadioButton
                >
              </ElRadioGroup>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.reason') }}</div></th
            >
            <td>
              <LangItem v-model="balance_params.description" />
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <span :class="[$style.button, $style.black]">
                <input type="submit" :value="$t('admin.execution')" />
              </span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>

    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['ipaddress', $t('admin.ipaddress')],
                      ['user_agent', $t('admin.user_agent')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('admin.use_statistics_exclude') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('admin.member_is_alive') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.connect') }}</div></th
            >
            <td>
              <el-select
                v-model="search_params.reference"
                filterable
                :placeholder="$t('admin.all')"
              >
                <el-option
                  v-for="val in [
                    [undefined, $t('admin.total')],
                    ...constants.log.log.target,
                  ]"
                  :key="val[0]"
                  :value="val[0]"
                  :label="val[1]"
                >
                </el-option>
              </el-select>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.connection_number') }}</div></th
            >
            <td>
              <input
                v-model="search_params.reference_seq"
                type="text"
                :class="[$style.inputTypeText, $style.w80]"
              />
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['last_login', $t('admin.last_login')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="
                  search_params = {}
                  search
                "
                ><span>{{ $t('admin.reset') }}</span></a
              >&nbsp;<span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin.change_history') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <table cellspacing="0" cellpadding="0" :class="$style.adminTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.performer') }}</div></th
          >
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.perfor_ip') }}</div></th
          >
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.case_name') }}</div></th
          >
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.occurrence_date') }}</div></th
          >
          <th scope="col" rowspan="2"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.connect') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.connection_number') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.before_change') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.after_change') }}</div></th
          >
        </tr>
        <tr>
          <th scope="col" colspan="3"
            ><div>{{ $t('admin.explanation') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.car') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <template v-for="data in list">
          <tr :key="`${data.seq}-1`" :class="[{ [$style.row2]: data._no % 2 }]">
            <td :rowspan="2" :class="[$style.center, $style.nowrap]">{{
              data.eventer_seq == -1 ? $t('admin.system') : data.user_id
            }}</td>
            <td :rowspan="2" :class="[$style.center, $style.nowrap]">{{
              long2ip(data.ipaddress)
            }}</td>
            <td :rowspan="2" :class="[$style.nowrap]">{{
              data.logset_description_replaced
            }}</td>
            <td :rowspan="2" :class="[$style.center, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.regdate))
            }}</td>
            <td :class="[$style.center, $style.nowrap]" rowspan="2">{{
              data._no
            }}</td>
            <td :class="[$style.center, $style.nowrap]">{{
              fromPairs(constants.log.log.target)[data.reference]
            }}</td>
            <td :class="[$style.center]">{{ data.reference_seq }}</td>
            <td :class="[$style.right, $style.number]">{{
              data.before | trimDecimalZero | numberFormat
            }}</td>
            <td :class="[$style.right, $style.number]">{{
              data.after | trimDecimalZero | numberFormat
            }}</td>
          </tr>
          <tr :key="`${data.seq}-2`" :class="[{ [$style.row2]: data._no % 2 }]">
            <td colspan="3" :class="[$style.wide, $style.nowrap]">{{
              data.description
                ? data.description_replaced
                : $t('admin.not_recorded')
            }}</td>
            <td
              :class="[
                $style.right,
                $style.number,
                $style.nowrap,
                $style[data.heading],
              ]"
            >
              <span :class="$style.fl">{{
                { eq: '', up: '+', down: '-' }[data.heading]
              }}</span>
              {{ data.diff | trimDecimalZero | numberFormat }}
            </td>
          </tr>
        </template>
        <tr v-if="loading">
          <td :colspan="9" :class="$style.tCenter">{{
            $t('admin.loading')
          }}</td>
        </tr>
        <tr v-else-if="!list.length">
          <td :colspan="10" :class="[$style.tCenter, $style.center]">{{
            $t('admin.msg_searched_change')
          }}</td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.crossTable th,
.crossTable td {
  vertical-align: middle;
}

td.number {
  font-family: monospace !important;
  font-size: 14px !important;
  font-weight: bold !important;
}

td.number.up {
  color: red !important;
}
td.number.down {
  color: blue !important;
}
td.number.eq {
  color: #000 !important;
}
</style>
